/**
 * Job Description Parser API Client.
 *
 * Wraps the existing apiClient for all job-description parsing endpoints.
 * Uses the same authenticated request pattern as other API modules.
 *
 * Endpoints:
 * - POST   /api/v1/job-descriptions              -- Parse a new job description
 * - GET    /api/v1/job-descriptions               -- List user's parsed JDs
 * - GET    /api/v1/job-descriptions/:id           -- Get a single parsed JD
 * - DELETE /api/v1/job-descriptions/:id           -- Delete a parsed JD
 * - POST   /api/v1/job-descriptions/:id/create-site -- Create resume site from JD
 */
import { apiClient } from './api.client';
import { logger } from './logger.client';
import type {
  JobDescription,
  ParseJobDescriptionInput,
  CreateSiteFromJDInput,
} from '../types/job-description.types';

const log = logger.child({ module: 'jobDescriptionApi' });

/** Server envelope shape for successful responses. */
interface ApiEnvelope<T> {
  data: T;
}

/** Server envelope shape for error responses. */
interface ApiErrorEnvelope {
  error: string;
  errorCode?: string;
  /** Record ID when a DB record was created before the failure (e.g. AI parse error). */
  id?: string;
}

export const jobDescriptionApi = {
  /**
   * Parse a job description from text or URL.
   *
   * Uses a non-blocking error pattern (like AI endpoints) since parsing
   * depends on the AI subsystem and can fail gracefully.
   *
   * @param input - Either { source_type: 'text', raw_text } or { source_type: 'url', source_url }
   * @returns Parsed JobDescription on success, or { error } on failure
   */
  parse: async (
    input: ParseJobDescriptionInput,
  ): Promise<JobDescription | { error: string; errorCode?: string; id?: string }> => {
    try {
      const response = await apiClient<ApiEnvelope<JobDescription> | ApiErrorEnvelope>(
        '/api/v1/job-descriptions',
        { method: 'POST', body: JSON.stringify(input) },
      );

      if ('error' in response) {
        const errResp = response as ApiErrorEnvelope;
        log.warn('Job description parse returned error', { error: errResp.error, id: errResp.id });
        return { error: errResp.error, errorCode: errResp.errorCode, id: errResp.id };
      }

      return response.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';

      if (message.includes('503')) {
        log.warn('AI service unavailable for JD parsing (503)');
        return { error: 'AI features are not available' };
      }

      log.error('Job description parse failed', { error: message });
      return { error: message };
    }
  },

  /**
   * List all parsed job descriptions for the current user.
   *
   * @returns Array of JobDescription entities, newest first
   */
  list: async (): Promise<JobDescription[]> => {
    const response = await apiClient<ApiEnvelope<JobDescription[]>>(
      '/api/v1/job-descriptions',
    );
    return response.data;
  },

  /**
   * Get a single parsed job description by ID.
   *
   * @param id - Job description UUID
   * @returns The full JobDescription entity
   */
  get: async (id: string): Promise<JobDescription> => {
    const response = await apiClient<ApiEnvelope<JobDescription>>(
      `/api/v1/job-descriptions/${encodeURIComponent(id)}`,
    );
    return response.data;
  },

  /**
   * Delete a parsed job description.
   *
   * @param id - Job description UUID
   */
  delete: async (id: string): Promise<void> => {
    await apiClient(
      `/api/v1/job-descriptions/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },

  /**
   * Create a new resume site pre-populated from a parsed job description.
   *
   * @param id - Job description UUID
   * @param input - Optional template selection
   * @returns The newly created site's id, name, and domain
   */
  createSite: async (
    id: string,
    input?: CreateSiteFromJDInput,
  ): Promise<{ id: string; name: string; domain: string }> => {
    const response = await apiClient<ApiEnvelope<{ id: string; name: string; domain: string }>>(
      `/api/v1/job-descriptions/${encodeURIComponent(id)}/create-site`,
      { method: 'POST', body: JSON.stringify(input ?? {}) },
    );
    return response.data;
  },
};
